import classNames from 'classnames';
import React from 'react';
import { COLORS } from 'shared';
import { ReactComponent as DefaultAvatar } from 'shared/assets/default-avatar.svg';

import { IconName } from 'shared';
import Icon from './Icon';
import Layout from './Layout';
import { LayoutColor } from './types';

export interface Props {
  size?: number;
  avatarUrl?: string | null;
  borderColor?: string;
  borderWidth?: 0 | 1 | 2 | 4;
  hoverBorderWidth?: 1 | 2 | 4;
  outlineWidth?: 1 | 2;
  outlineColor?: string;
  defaultIcon?: IconName;
  /** When defaultIcon is set, color for the placeholder icon (e.g. gray-200 to match image placeholders). */
  defaultIconColor?: LayoutColor;
  alt?: string;
  hexColor?: string;
  opacity?: number;
  hasHoverStyle?: boolean;
  firstname?: string;
  backgroundColor?: LayoutColor;
}

const Avatar: React.FC<Props> = ({
  size = 160,
  avatarUrl,
  borderColor = 'white',
  outlineWidth = 0,
  outlineColor = 'transparent',
  borderWidth = 1,
  hoverBorderWidth,
  defaultIcon,
  defaultIconColor,
  alt = 'user avatar',
  hexColor = COLORS.white,
  opacity,
  hasHoverStyle,
  backgroundColor = 'gray-25'
}) => {
  // Calculate icon size based on avatar size (roughly 50% of avatar size)
  const iconSize = typeof size === 'number' ? Math.floor(size * 0.5) : 'md';

  const getDefaultAvatar = () => {
    if (defaultIcon) {
      return <Icon name={defaultIcon} size={iconSize} color={defaultIconColor ?? 'gray-400'} />;
    }

    return <DefaultAvatar style={{ width: '50%', height: '50%' }} fill={hexColor} />;
  };

  const shouldShowDefault = !avatarUrl;

  return (
    <Layout
      __style={{
        background: avatarUrl ? borderColor : undefined,
        border: `${outlineWidth}px solid ${outlineColor}`
      }}
      color={shouldShowDefault ? backgroundColor : undefined}
      justify="center"
      align="center"
      width={size}
      height={size}
      overflow="hidden"
      borderRadius="circular"
      position="relative"
      __className="Avatar"
      opacity={opacity}
    >
      {shouldShowDefault && getDefaultAvatar()}
      {avatarUrl && (
        <img
          style={{
            objectFit: 'cover',
            borderRadius: '50%'
          }}
          className={classNames(
            'Avatar-image',
            `is-avatar-border-width-${borderWidth}`,
            hoverBorderWidth ? `is-avatar-hover-border-width-${hoverBorderWidth}` : undefined
          )}
          src={avatarUrl}
          alt={alt}
        />
      )}
      {hasHoverStyle && (
        <Layout
          position="absolute"
          width="100%"
          height="100%"
          top={0}
          left={0}
          borderRadius="circular"
          hoverColor="gray-950"
          opacity={0.2}
        />
      )}
    </Layout>
  );
};

export default Avatar;
